/**
 * useSeoMeta - updates document.title + all head meta tags for tour pages.
 * Call setTourMeta() once the tour data is available (from API or TourViewer emit).
 */

import { isMlsPresentationMode, type ViewerPresentationMode } from '../utils/presentationMode'

const DEFAULT_TITLE = 'Virtual Tour | VirtualTourEasy'
const DEFAULT_DESCRIPTION = 'View this 360 virtual tour powered by VirtualTourEasy.'
const DEFAULT_IMAGE = 'https://virtualtoureasy.com/images/og-image.jpg'
const SITE_NAME = 'VirtualTourEasy'
const MLS_TITLE = '360 Virtual Tour'
const MLS_DESCRIPTION = 'Unbranded virtual tour media'

function setMeta(property: string, content: string, isName = false) {
  const attr = isName ? 'name' : 'property'
  let el = document.querySelector(`meta[${attr}="${property}"]`)
  if (!el) {
    el = document.createElement('meta')
    el.setAttribute(attr, property)
    document.head.appendChild(el)
  }
  el.setAttribute('content', content)
}

function removeMeta(property: string, isName = false) {
  const attr = isName ? 'name' : 'property'
  document.querySelectorAll(`meta[${attr}="${property}"]`).forEach(el => el.remove())
}

export function setTourMeta(params: {
  title?: string | null
  description?: string | null
  image?: string | null
  url?: string
  orgName?: string | null
  presentationMode?: ViewerPresentationMode
}) {
  const { title, description, image, url, orgName, presentationMode = 'default' } = params
  const isMlsMode = isMlsPresentationMode(presentationMode)

  const displayTitle = isMlsMode
    ? MLS_TITLE
    : title
    ? `${title} | ${SITE_NAME}`
    : orgName
    ? `Virtual Tour by ${orgName} | ${SITE_NAME}`
    : DEFAULT_TITLE

  const displayDescription = isMlsMode ? MLS_DESCRIPTION : (description || DEFAULT_DESCRIPTION)
  const displayImage = image || DEFAULT_IMAGE
  const displayUrl = url || window.location.href

  document.title = displayTitle

  setMeta('description', displayDescription, true)

  setMeta('og:title', displayTitle)
  setMeta('og:description', displayDescription)
  setMeta('og:image', displayImage)
  setMeta('og:url', displayUrl)
  setMeta('og:type', 'website')
  if (isMlsMode) {
    removeMeta('og:site_name')
    setMeta('robots', 'noindex, nofollow', true)
  } else {
    setMeta('og:site_name', SITE_NAME)
  }

  setMeta('twitter:card', 'summary_large_image', true)
  setMeta('twitter:title', displayTitle, true)
  setMeta('twitter:description', displayDescription, true)
  setMeta('twitter:image', displayImage, true)
}

/** Meta tags for a public video page (og:video + twitter player card). */
export function setVideoMeta(params: {
  title?: string | null
  description?: string | null
  image?: string | null
  url?: string
  orgName?: string | null
  video?: { url?: string | null; mime?: string | null; width?: number | null; height?: number | null; embedUrl?: string | null } | null
}) {
  const { title, description, image, url, orgName, video } = params
  const displayTitle = title ? `${title} | ${SITE_NAME}` : orgName ? `Video by ${orgName} | ${SITE_NAME}` : `Property video | ${SITE_NAME}`
  const displayDescription = description || 'Watch this property video and explore the 360° virtual tour.'
  const displayImage = image || DEFAULT_IMAGE
  const displayUrl = url || window.location.href

  document.title = displayTitle
  setMeta('description', displayDescription, true)
  setMeta('og:title', displayTitle)
  setMeta('og:description', displayDescription)
  setMeta('og:image', displayImage)
  setMeta('og:url', displayUrl)
  setMeta('og:type', 'video.other')
  setMeta('og:site_name', SITE_NAME)
  if (video?.url) {
    setMeta('og:video', video.url)
    setMeta('og:video:secure_url', video.url)
    setMeta('og:video:type', video.mime || 'video/mp4')
    if (video.width) setMeta('og:video:width', String(video.width))
    if (video.height) setMeta('og:video:height', String(video.height))
  }
  setMeta('twitter:card', video?.embedUrl ? 'player' : 'summary_large_image', true)
  setMeta('twitter:title', displayTitle, true)
  setMeta('twitter:description', displayDescription, true)
  setMeta('twitter:image', displayImage, true)
  if (video?.embedUrl) {
    setMeta('twitter:player', video.embedUrl, true)
    setMeta('twitter:player:width', String(video.width || 1280), true)
    setMeta('twitter:player:height', String(video.height || 720), true)
  }
}

export function resetMeta() {
  document.title = DEFAULT_TITLE
}
